import React, { FC, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import DatePickerDefault, { registerLocale } from 'react-datepicker';
import { IControlProps, IMarketHelper } from '../types/IFormSettings';
import { updateFormControlValueAction } from '../actions/formActions';
// @ts-ignore
import { controlSelector, formKeySelector } from '../selectors/formBuilder.selector';
import {
    getDateFromProps,
    convertToDefaultLocalDate,
    toUTCDate,
    dateMarketFormat,
    dateFormatPlaceholderRemapper,
    marketRegex,
    mainDateDelimiter,
    dateIndexPositions,
} from '../helpers';
import 'react-datepicker/dist/react-datepicker.css';
import de from "date-fns/locale/de";
// import es from "date-fns/locale/es";
// import et from "date-fns/locale/et";
// import fi from "date-fns/locale/fi";
// import lt from "date-fns/locale/lt";
// import nn from "date-fns/locale/nn";
// import sv from "date-fns/locale/sv";
import '../styles/DatePicker.scss';

registerLocale("de-DE", de)
registerLocale("de", de)
// registerLocale("et-EE", et)
// registerLocale("et", et)
// registerLocale("es-ES", es)
// registerLocale("es", es)
// registerLocale("fi-FI", fi)
// registerLocale("fi", fi)
// registerLocale("lt-LT", lt)
// registerLocale("lt", lt)
// registerLocale("no-NO", nn)
// registerLocale("no", nn)
// registerLocale("sv-SV", sv)
// registerLocale("sv", sv)

const DatePicker: FC<IControlProps> = ({ controlName, formName }) => {
    const dispatch = useDispatch();
    const { value: currentDate, readonly, minDate, maxDate, openToDate, disableCalendar } = useSelector(
        controlSelector(formName, controlName),
    );
    const [touched, setTouched] = useState(false);
    const [workValue, setWorkValue] = useState<Date | [Date, Date] | null>(null);
    const formMeta = useSelector(formKeySelector(formName, 'formMeta'));
    const minimumDate: Date = getDateFromProps(minDate) ?? new Date(1940, 0, 1);
    const maximumDate: Date = getDateFromProps(maxDate) ?? new Date(2099, 0, 1);
    const marketMeta: IMarketHelper = {
        dateFormat: dateMarketFormat(formMeta?.market ?? ''),
        marketRegex: marketRegex(formMeta?.market ?? ''),
        mainDelimiter: mainDateDelimiter(formMeta?.market ?? ''),
        indexPositions: dateIndexPositions(formMeta?.market ?? '')
    }

    // Could possibly move this validation to outer logic for consistency. As well as displaying a better error message than "Required" <-- which is the only possible one at this point. (But all this is NOT vital a.t.m.)
    const isBetween = (dateToCheck: Date) => (dateToCheck.getTime() >= minimumDate.getTime() && dateToCheck.getTime() <= maximumDate.getTime());

    const onChangeHandler = (date: Date | [Date, Date] /* for selectsRange */ | null) => {
        const selected = Array.isArray(date) ? date[0] : date;
        const utcDate = selected ? toUTCDate(selected) : date;
        setTouched(true);
        setWorkValue(utcDate);
    }

    const onChangeRawHandler = (event: React.FocusEvent<HTMLInputElement>) => {
        if(!event.target.value) return;
        if(typeof event.target.value === 'string') {
            event.target.value = event.target.value.replace(/[^0-9]+/g, marketMeta.mainDelimiter);
        }

        let value: string | Date = event.target.value;

        // Checking the length like so will avoid the invalid state until user has been given a fair shot at entering a valid date, hence improving UX.
        if(value && value.length > 9) {
            setTouched(true);
        }

        if (new RegExp(marketMeta.marketRegex).test(value)) {
            const parts = value.split(marketMeta.mainDelimiter).map((p) => parseInt(p, 10));
            value = '';
            parts[marketMeta.indexPositions.month] -= 1; // Subtract month by one to correspond to *index* and NOT natural month numbering.
            const comparisonDate = new Date(parts[marketMeta.indexPositions.year], parts[marketMeta.indexPositions.month], parts[marketMeta.indexPositions.day]);
            if(comparisonDate.getMonth() === parts[marketMeta.indexPositions.month] && comparisonDate.getDate() === parts[marketMeta.indexPositions.day] && comparisonDate.getFullYear() === parts[marketMeta.indexPositions.year]) {
                value = toUTCDate(new Date(comparisonDate.getTime() + 3600000)); // Add 1h "hack" to avoid calendar picking the date before the very chosen one when using ENTER.
            }
        }

        if(value instanceof Date && isBetween(value)) {
            setWorkValue(value);
            return;
        }

        setWorkValue(null);
    }

    useEffect(() => {
        if (touched) {
            dispatch(
                updateFormControlValueAction({
                    formName,
                    value: workValue,
                    controlName,
                }),
            );
        }
    }, [controlName, dispatch, formName, workValue, touched]);

    return (
        <div className={disableCalendar ? `noCalendar` : ``}>
             <DatePickerDefault
              id={controlName}
              name={controlName}
              className="form-control"
              disabled={readonly}
              selected={convertToDefaultLocalDate(currentDate, (formMeta?.market ?? ''))}
              minDate={minimumDate}
              maxDate={maximumDate}
              openToDate={getDateFromProps(openToDate) ?? undefined}
              dateFormat={marketMeta.dateFormat}
              placeholderText={dateFormatPlaceholderRemapper(marketMeta.dateFormat, (formMeta?.market ?? ''))}
              onChange={onChangeHandler} // Component's onChange is vital for *picking* a date.
              onChangeRaw={onChangeRawHandler} // onChangeRaw is used to handle keyb in order to catch bad input: Which actually improves UX by showing the error for customer, which the component most often by itself still would parse to a valid, but unintentional, date. Which would be really bad UX.
              popperPlacement={"auto"}
              locale={formMeta?.locale}
              showMonthDropdown
              showYearDropdown
              scrollableYearDropdown
              yearDropdownItemNumber={100} // Drastically improves UX by showing many choises at once
              fixedHeight
              strictParsing={true} // Without this even more obscure input combinations will parse into a valid date, so we want this here to narrow down what counts as a valid date.
             />
        </div>
    );
};

export default DatePicker;
